/* eslint-disable @typescript-eslint/no-explicit-any */
import { ActionContext } from 'vuex';

import api from '@/api';
import { ApiReportDetailsItem } from '@/api/types/report';
import { format } from 'date-fns';
import { DataTab } from './device';

function removeSecondsFromTimeString(timeString) {
    const [hours, minutes] = timeString.split(':');
    return `${hours}:${minutes}`;
}

interface ReportDataItem {
    list: Array<any>;
    columns: Array<any>;
}

interface ReportState {
    ReportDetailsList: Array<ApiReportDetailsItem>;
    data: {
        list: Array<any>;
        columns: Array<any>;
    };
    listRequestPending: boolean;
    activeReport: string | null;
    dateFrom: string;
    dateTo: string;
    deviceIds: Array<number>;
    cardType: DataTab;
    tableSettings: any;
    dataStoredForUser: number | null;
}

const report = {
    namespaced: true,
    state: {
        ReportDetailsList: [],
        data: {
            list: [],
            columns: [],
        },
        listRequestPending: false,
        activeReport: null,
        dateFrom: format(new Date(), 'yyyy-MM-dd'),
        dateTo: format(new Date(), 'yyyy-MM-dd'),
        deviceIds: [],
        cardType: DataTab.Devices,
        tableSettings: {},
        dataStoredForUser: null,
    },
    getters: {
        reportDetailsList(state: ReportState): Array<ApiReportDetailsItem> {
            return state.ReportDetailsList;
        },
        data(state: ReportState): ReportDataItem {
            return state.data;
        },
        dateFrom(state: ReportState): string {
            return state.dateFrom;
        },
        dateTo(state: ReportState): string {
            return state.dateTo;
        },
        deviceIds(state: ReportState): Array<number> {
            return state.deviceIds;
        },
        activeReport(state: ReportState): string {
            return state.activeReport;
        },
        cardType(state: ReportState): string {
            return state.cardType;
        },
        tableSettings(state: ReportState): any {
            return state.tableSettings;
        },
        dataStoredForUser(state: ReportState): number | null {
            return state.dataStoredForUser;
        },
    },
    mutations: {
        startListRequest(state: ReportState): void {
            state.listRequestPending = true;
        },
        endListRequest(state: ReportState): void {
            state.listRequestPending = false;
        },
        updateDataList(state: ReportState, data: ReportDataItem): void {
            state.data = data;
        },
        updateReportsList(state: ReportState, list: Array<any>): void {
            state.ReportDetailsList = list;
        },
        updateDeviceIds(state: ReportState, devices: Array<number>): void {
            state.deviceIds = devices;
        },
        updateDateFrom(state: ReportState, date: string): void {
            state.dateFrom = date;
        },
        updateDateTo(state: ReportState, date: string): void {
            state.dateTo = date;
        },
        updateActiveReport(state: ReportState, activeReport: string): void {
            state.activeReport = activeReport;
        },
        storeTableSettings(state: ReportState, tableSettings: any): void {
            state.tableSettings = tableSettings;
        },
        updateUserId(state: ReportState, userId: number): void {
            state.dataStoredForUser = userId;
        },
        changeTabType(state: ReportState, type: DataTab): void {
            state.cardType = type;
        },
        clearReportsList(state: ReportState): void {
            state.ReportDetailsList = [];
        },
        clearActiveReport(state: ReportState): void {
            state.activeReport = '';
        },
        clearStoreTableSettings(state: ReportState): void {
            state.tableSettings = {};
        },
        clearDeviceIds(state: ReportState): void {
            state.deviceIds = [];
        },
        clearState(state: ReportState): void {
            state.ReportDetailsList = [];
            state.data = {
                list: [],
                columns: [],
            };
            state.listRequestPending = false;
            state.activeReport = null;
            state.dateFrom = format(new Date(), 'yyyy-MM-dd');
            state.dateTo = format(new Date(), 'yyyy-MM-dd');
            state.deviceIds = [];
            state.cardType = DataTab.Devices;
            state.tableSettings = {};
            state.dataStoredForUser = null;
        },
    },
    actions: {
        async list(context: ActionContext<ReportState, null>): Promise<void> {
            context.commit('startListRequest');
            try {
                const token = window.localStorage.getItem('authcredentials');
                const response = await api.report.list(token || '');
                const reports = response.data.ReportDetails.filter((r) => r.ReportResultType === 'Table').map((r) => ({
                    value: r.Code,
                    text: r.Caption,
                }));

                context.commit('updateReportsList', reports);
            } catch (err) {
                // console.log(err);
            } finally {
                context.commit('endListRequest');
            }
        },
        async table(context: ActionContext<ReportState, null>): Promise<void> {
            context.commit('startListRequest');
            try {
                const token = window.localStorage.getItem('authcredentials');
                const response = await api.report.getTable(token || '', {
                    ReportCode: context.state.activeReport,
                    from: context.state.dateFrom,
                    until: context.state.dateTo,
                    deviceIds: context.state.deviceIds,
                });
                const reportList = response.data?.TableData.List.map((r, index) => ({
                    ...r,
                    ID: index + 1,
                    Route_Date: format(new Date(r.Route_Date), 'yyyy-MM-dd'),
                    Route_Duration: removeSecondsFromTimeString(r.Route_Duration),
                    Route_Start_Time: removeSecondsFromTimeString(r.Route_Start_Time),
                    Route_End_Time: removeSecondsFromTimeString(r.Route_End_Time),
                }));

                context.commit('updateDataList', {
                    list: reportList,
                    columns: response.data?.TableData.Fields,
                });
            } catch (err) {
                // console.log(err);
            } finally {
                context.commit('endListRequest');
            }
        },
    },
};

export default report;
